import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Truck, Copy, Send, Building2 } from "lucide-react";
import { COURIERS, DEFAULT_COURIER_KEY, courierName, trackingUrl } from "../../lib/couriers";

const STATUSES = ["placed","confirmed","packed","shipped","out_for_delivery","delivered","cancelled","returned","refunded"];
const COLOR = {delivered:"default", confirmed:"secondary", shipped:"secondary", cancelled:"destructive", returned:"destructive"};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [b2bOnly, setB2bOnly] = useState(false);
  const [sel, setSel] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [awb, setAwb] = useState("");
  const [courier, setCourier] = useState(DEFAULT_COURIER_KEY);

  const load = () => api.get(`/admin/orders${filter!=="all"?`?status=${filter}`:""}`).then(r=>setOrders(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const setStatus = async () => {
    if (!newStatus) return;
    await api.post(`/admin/orders/${sel.order_id}/status`, { status: newStatus });
    toast.success(`Order ${sel.order_no} → ${newStatus}`);
    setSel(null); setNewStatus(""); load();
  };

  const saveAwb = async () => {
    try {
      const r = await api.post(`/admin/orders/${sel.order_id}/awb`, { awb_number: awb.trim(), courier });
      toast.success(awb.trim() ? `AWB saved for ${sel.order_no} via ${courierName(courier)}` : `AWB cleared for ${sel.order_no}`);
      setSel(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save AWB"); }
  };

  const resendConfirmation = async () => {
    if (!window.confirm(`Send Order Confirmation SMS to ${sel.phone || "the customer"} for ${sel.order_no}?`)) return;
    try {
      const r = await api.post(`/admin/orders/${sel.order_id}/resend-confirmation`);
      toast.success(`Confirmation SMS sent · ${r.data.sms_status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send confirmation"); }
  };

  const resendTracking = async () => {
    if (!window.confirm(`Re-send tracking SMS to ${sel.phone || "the customer"} for ${sel.order_no}?`)) return;
    try {
      const r = await api.post(`/admin/orders/${sel.order_id}/resend-tracking`);
      toast.success(`Tracking SMS re-sent · ${r.data.sms_status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend tracking"); }
  };

  const requestPayment = async () => {
    if (!window.confirm(`Send pay-link SMS to ${sel.phone || "the customer"} for ${sel.order_no}?`)) return;
    try {
      const r = await api.post(`/admin/orders/${sel.order_id}/request-payment`);
      toast.success(`Pay-link SMS sent · ${r.data.sms_status}`);
      setSel(s => ({ ...s, payment_status: "awaiting_payment" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send pay-link"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-outfit font-bold">Orders</h1>
          <div className="text-sm text-stone-500">
            {(() => {
              const shown = orders.filter(o => !b2bOnly || (o.buyer_gstin || "").trim());
              return `${shown.length}${b2bOnly ? ` B2B` : ""} of ${orders.length} orders`;
            })()}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-white border border-stone-200 rounded-full px-4 py-2 hover:border-[#D4AF37]"
                 data-testid="b2b-filter-label">
            <input type="checkbox" checked={b2bOnly} onChange={e=>setB2bOnly(e.target.checked)}
                    data-testid="b2b-filter" className="w-4 h-4 accent-[#3B2F2F]"/>
            <Building2 className="w-3.5 h-3.5 text-[#D4AF37]"/>
            <span>B2B only (with GSTIN)</span>
          </label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-white" data-testid="order-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Order #</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>GSTIN</TableHead>
            <TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
            <TableHead>AWB</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {orders.filter(o => !b2bOnly || (o.buyer_gstin || "").trim()).map(o => (
              <TableRow key={o.order_id} onClick={()=>{setSel(o); setNewStatus(o.status); setAwb(o.awb_number || ""); setCourier(o.courier || DEFAULT_COURIER_KEY);}} className="cursor-pointer hover:bg-stone-50" data-testid={`order-row-${o.order_no}`}>
                <TableCell className="font-medium">{o.order_no}</TableCell>
                <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-xs">
                  <div className="font-medium text-stone-800" data-testid={`order-customer-name-${o.order_no}`}>{o.customer_name || o.email || "—"}</div>
                  {o.customer_name && o.email && <div className="text-[10px] text-stone-400">{o.email}</div>}
                  {o.buyer_company_name && <div className="text-[10px] text-[#D4AF37] uppercase tracking-wide">{o.buyer_company_name}</div>}
                </TableCell>
                <TableCell className="text-xs font-mono text-stone-700" data-testid={`order-customer-phone-${o.order_no}`}>
                  {o.phone ? o.phone : <span className="text-stone-300">—</span>}
                </TableCell>
                <TableCell className="font-mono text-[11px]">
                  {o.buyer_gstin ? <span className="text-[#2E4A35]">{o.buyer_gstin}</span> : <span className="text-stone-300">—</span>}
                </TableCell>
                <TableCell>{o.items.length}</TableCell>
                <TableCell className="font-outfit">{inr(o.total)}</TableCell>
                <TableCell><Badge variant={COLOR[o.status] || "outline"}>{o.status}</Badge></TableCell>
                <TableCell className="font-mono text-xs text-stone-600 max-w-[10rem] truncate" title={o.awb_number || ""}>{o.awb_number || <span className="text-stone-300">—</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!sel} onOpenChange={(v)=>!v&&setSel(null)}>
        <DialogContent className="max-w-2xl">
          {sel && (<>
            <DialogHeader><DialogTitle>Order {sel.order_no}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-stone-500">Customer:</span> {sel.email}</div>
                <div><span className="text-stone-500">Total:</span> {inr(sel.total)}</div>
                <div><span className="text-stone-500">Phone:</span> {sel.phone}</div>
                <div><span className="text-stone-500">Address:</span> {sel.address?.city}, {sel.address?.state}</div>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2">Items</div>
                {sel.items.map((i,k)=><div key={k} className="text-sm flex justify-between"><span>{i.qty}× {i.product_name} ({i.variant_label})</span><span>{inr(i.line_total)}</span></div>)}
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2 flex items-center gap-1.5"><Copy className="w-3 h-3"/> Pay-online link (share via SMS/WhatsApp)</div>
                {sel.payment_status === "paid" ? (
                  <div className="text-xs text-[#2E4A35]">This order is already marked paid.</div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Input readOnly value={`${window.location.origin}/pay/${sel.order_id}`}
                              data-testid="order-detail-paylink"
                              className="flex-1 font-mono text-xs bg-stone-50"/>
                      <button onClick={()=>{
                        const link = `${window.location.origin}/pay/${sel.order_id}`;
                        navigator.clipboard.writeText(link);
                        toast.success("Payment link copied to clipboard");
                      }} data-testid="order-detail-paylink-copy" className="bg-stone-100 text-stone-700 rounded-md px-4 text-sm flex items-center gap-1 hover:bg-stone-200">
                        <Copy className="w-3 h-3"/> Copy
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={requestPayment} data-testid="order-detail-send-paylink"
                              disabled={!sel.phone}
                              className="flex-1 bg-[#3B2F2F] text-white rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-black disabled:opacity-50">
                        <Send className="w-3.5 h-3.5"/>
                        {sel.payment_status === "awaiting_payment" ? "Resend pay-link SMS" : "Send pay-link SMS to customer"}
                      </button>
                      {sel.payment_status === "awaiting_payment" && (
                        <span className="text-[11px] px-2 py-1 rounded bg-amber-100 text-amber-800 font-medium">Awaiting payment</span>
                      )}
                    </div>
                    {!sel.phone && <div className="text-[11px] text-[#B00020] mt-1">No phone number on this order — SMS unavailable.</div>}
                  </>
                )}
                <div className="text-[11px] text-stone-500 mt-1.5">Customer opens the link, pays via Razorpay, and the order flips to Paid automatically.</div>
              </div>

              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2 flex items-center gap-1.5"><Send className="w-3 h-3"/> Order Confirmation SMS</div>
                <button onClick={resendConfirmation} data-testid="order-detail-send-confirmation"
                        disabled={!sel.phone}
                        className="w-full bg-stone-100 text-stone-800 rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-stone-200 disabled:opacity-50">
                  <Send className="w-3.5 h-3.5"/> Send confirmation SMS to customer
                </button>
                <div className="text-[11px] text-stone-500 mt-1.5">Auto-sends when an order is placed (online + COD + offline). Use this to nudge a customer who says they never received the message. Rate-limited to 3/hour per order.</div>
              </div>

              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2 flex items-center gap-1.5"><Truck className="w-3 h-3"/> AWB / Tracking</div>
                <div className="flex gap-2">
                  <Select value={courier} onValueChange={setCourier}>
                    <SelectTrigger className="w-40" data-testid="order-detail-courier-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {COURIERS.map(c => <SelectItem key={c.key} value={c.key} data-testid={`courier-opt-${c.key}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={awb} onChange={e=>setAwb(e.target.value)} placeholder="AWB number"
                          data-testid="order-detail-awb-input"
                          className="flex-1 font-mono text-sm"/>
                  <button onClick={saveAwb} data-testid="order-detail-awb-save" className="bg-[#3B2F2F] text-white rounded-md px-4 text-sm">Save AWB</button>
                </div>
                {(sel.awb_number || "").trim() && (
                  <div className="mt-2 space-y-1">
                    <a href={trackingUrl(sel.courier || courier, sel.awb_number)} target="_blank" rel="noreferrer"
                       data-testid="order-detail-track-link"
                       className="text-xs text-[#3B2F2F] underline hover:text-[#D4AF37] break-all">
                      Track: {trackingUrl(sel.courier || courier, sel.awb_number)}
                    </a>
                    <button onClick={resendTracking} data-testid="order-detail-resend-tracking"
                            disabled={!sel.phone}
                            className="w-full bg-stone-100 text-stone-800 rounded-md px-4 py-2 text-sm flex items-center justify-center gap-2 hover:bg-stone-200 disabled:opacity-50">
                      <Send className="w-3.5 h-3.5"/> Re-send tracking SMS via {courierName(sel.courier || courier)}
                    </button>
                  </div>
                )}
                <div className="text-[11px] text-stone-500 mt-1.5">Pick the courier partner before saving. Changing the courier + saving re-sends the tracking SMS with the correct URL.</div>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2">Update status</div>
                <div className="flex gap-2">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="flex-1" data-testid="update-status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={setStatus} data-testid="update-status-btn" className="bg-[#3B2F2F] text-white rounded-md px-4">Update</button>
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-stone-500 mb-2">Timeline</div>
                <div className="space-y-2">{sel.timeline?.map((t,i)=><div key={i} className="text-xs flex gap-2"><span className="text-[#D4AF37]">●</span><span>{t.status}</span><span className="text-stone-400 ml-auto">{new Date(t.at).toLocaleString()}</span></div>)}</div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
